import { useEffect, useState } from "react";
import { api, getApiError } from "@/lib/api";
import { fmtLog } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Mail, Sparkles, RefreshCw, AlertTriangle } from "lucide-react";
import { DOCS_ACCENT as ACCENT } from "@/lib/govDocs";

/**
 * Email office staff about a new Active version. The AI drafts the message from what
 * changed; the manager reviews and edits it before anything is sent.
 */
export default function AnnounceVersionDialog({ open, docId, versionId, versionLabel, onClose, onSent }) {
  const [draft, setDraft] = useState(null);     // { subject, message, ai, recipients, announced }
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.govAnnouncementDraft(docId, versionId)
      .then(setDraft)
      .catch((e) => { toast.error(getApiError(e, "Couldn't draft the email.")); onClose(); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && versionId) { setDraft(null); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, versionId]);

  const send = async () => {
    if (draft.announced && !window.confirm("Staff have already been emailed about this version. Send it again?")) return;
    setSending(true);
    try {
      const r = await api.govAnnounce(docId, versionId, { subject: draft.subject, message: draft.message, resend: !!draft.announced });
      if (r.sent === r.recipients) toast.success(`Emailed ${r.sent} staff member${r.sent === 1 ? "" : "s"}.`);
      else toast.warning(`Sent to ${r.sent} of ${r.recipients} staff. The rest couldn't be sent: the email may be turned off under Reminder Emails, or check the Email Queue.`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(getApiError(e, "Couldn't send the email.")); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="announce-dialog">
        <DialogHeader>
          <DialogTitle className="font-brand text-base flex items-center gap-2">
            <Mail className="h-4 w-4" style={{ color: ACCENT }} /> Tell staff about version {versionLabel}?
          </DialogTitle>
          <DialogDescription className="text-xs">
            Review the message below. Nothing is sent until you click Send to staff.
          </DialogDescription>
        </DialogHeader>

        {loading || !draft ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-slate-500" data-testid="announce-loading">
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: ACCENT }} />
            Drafting a summary of the changes with AI…
          </div>
        ) : (
          <div className="space-y-3">
            {draft.announced && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Staff were already emailed about this version by {draft.announced.by_name} on {fmtLog(draft.announced.at)}.
              </div>
            )}
            <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1">
                {draft.ai
                  ? <><Sparkles className="h-3.5 w-3.5" style={{ color: ACCENT }} />Drafted by AI from the document and what changed. Check it's accurate before sending.</>
                  : <><AlertTriangle className="h-3.5 w-3.5 text-amber-600" />The AI couldn't draft this, so here's a starting point to edit.</>}
              </span>
              <button className="inline-flex items-center gap-1 hover:text-[#6b4f8a]" onClick={load} disabled={loading || sending} data-testid="announce-redraft">
                <RefreshCw className="h-3 w-3" /> Redraft
              </button>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Subject</Label>
              <Input value={draft.subject} maxLength={200} className="h-9 text-sm" data-testid="announce-subject"
                onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message</Label>
              <Textarea value={draft.message} rows={11} maxLength={5000} className="text-sm" data-testid="announce-message"
                onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))} />
              <div className="text-[11px] text-slate-400">
                Lines starting with "- " become bullet points. The email greets each person by name and links to the document.
              </div>
            </div>
            <div className="text-xs text-slate-600">
              Will be emailed to <strong>{draft.recipients}</strong> office staff member{draft.recipients === 1 ? "" : "s"} (Support Workers aren't included yet).
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={sending} data-testid="announce-skip">
            {draft?.announced ? "Close" : "Not now"}
          </Button>
          <Button size="sm" style={{ backgroundColor: ACCENT }} onClick={send} data-testid="announce-send"
            disabled={loading || sending || !draft || !draft.subject?.trim() || !draft.message?.trim() || !draft.recipients}>
            {sending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{draft?.announced ? "Send again" : "Send to staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
